import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  Icon,
  Switch,
  Textarea
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import { CancellationReason } from 'models/Invoice'
import { rgba } from 'polished'

import CanLogical from 'components/CanLogical'
import IconContainer from 'components/IconContainer/IconContainer'

import { toastProps } from 'utils/types/toast-props'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import {
  CancelConfirmationFormData,
  cancelConfirmationSchema
} from './CancelConfirmationDialog.schema'

const METRICS_HELPER_TEXT = {
  charge:
    'Quando ligado, o valor desta fatura continua somado em "A\u00A0receber" (em\u00A0Canceladas/Expiradas) e entra na inadimplência e no churn. Desligue se a fatura foi reemitida. Se o cliente pagou por fora, use "Marcar como paga" em vez de cancelar.',
  recurrence:
    'Quando ligado, o cancelamento entra no churn e as faturas emitidas e canceladas continuam somadas em "A\u00A0receber" (em\u00A0Canceladas/Expiradas).',
  installment:
    'Quando ligado, as faturas emitidas e canceladas do parcelamento continuam somadas em "A\u00A0receber" (em\u00A0Canceladas/Expiradas) e entram na inadimplência.'
}

interface CancelableNfse {
  uuid: string
  nfse_number: string | null
  status: string
}

interface CancelConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  billingUuid: string
  onSuccess?: (reloadTable?: boolean) => void
  type?: 'charge' | 'recurrence' | 'installment'
  cancellationData?: CancellationReason
  cancelableNfses?: CancelableNfse[]
}

const CancelConfirmationModal: React.FC<CancelConfirmationModalProps> = ({
  cancellationData,
  isOpen,
  onClose,
  billingUuid,
  onSuccess,
  type = 'charge',
  cancelableNfses
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const canCancelNfses = !!CanLogical({ permission: 'nfses:cancel' })
  const hasCancelableNfses = !!cancelableNfses && cancelableNfses.length > 0
  const showCancelNfsesToggle =
    type === 'charge' && hasCancelableNfses && canCancelNfses
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CancelConfirmationFormData>({
    defaultValues: {
      cancellationReason: 'other',
      countAsChurn: false,
      cancelNfses: false,
      notes: ''
    },
    resolver: yupResolver(cancelConfirmationSchema)
  })

  useEffect(() => {
    if (cancellationData?.uuid) {
      reset({
        cancellationReason: cancellationData.reasonType,
        countAsChurn: cancellationData.countAsChurn,
        notes: cancellationData.notes ?? ''
      })
    }
  }, [cancellationData, reset])
  // Podemos usar os dados do formulário para enviar ao backend se necessário

  const onSubmit = async (data: CancelConfirmationFormData) => {
    try {
      setLoading(true)
      const id = toast.loading(
        type === 'charge'
          ? 'Cancelando a fatura...'
          : type === 'recurrence'
          ? 'Cancelando a recorrência...'
          : 'Cancelando o parcelamento...'
      )

      const response = await DataService({
        type: type === 'installment' ? 'DELETE' : 'PATCH',
        data: {
          cancellation_reason: data.cancellationReason,
          count_in_metrics: data.countAsChurn,
          notes: data.notes,
          ...(type === 'charge' && showCancelNfsesToggle
            ? { cancel_nfses: data.cancelNfses }
            : {})
        },
        url:
          type === 'installment'
            ? `/v3/billings/installments/${billingUuid}`
            : `/v1/${
                type === 'charge' ? 'billings' : 'recurrences'
              }/${billingUuid}/cancel`
      })

      const errorMessage =
        response.error && (response.detail_error || response.message)

      toast.update(id, {
        render: response.error ? errorMessage : response.data.message,
        type: response.error ? 'error' : 'success',
        ...toastProps
      })

      if (!response.error) {
        const nfseResults = response.data?.nfse_cancellation_results as
          | Array<{
              uuid: string
              status: 'success' | 'error'
              error: string | null
            }>
          | undefined

        if (nfseResults && nfseResults.length > 0) {
          const failed = nfseResults.filter((r) => r.status === 'error')
          if (failed.length > 0) {
            toast.warning(
              failed.length === 1
                ? `Cobrança cancelada. Não foi possível cancelar a nota fiscal: ${failed[0].error}. Tente novamente em Notas Fiscais.`
                : 'Cobrança cancelada. Uma ou mais notas fiscais não foram canceladas. Tente novamente em Notas Fiscais.',
              toastProps
            )
          }
        }

        // Close the modal and notify parent of success
        onClose()
        if (onSuccess) {
          onSuccess()
        }
      }
    } catch (error) {
      toast.error(
        type === 'charge'
          ? 'Erro ao cancelar a fatura.'
          : 'Erro ao cancelar a recorrência.'
      )
      console.error(
        type === 'charge'
          ? 'Error cancelling billing:'
          : 'Error cancelling recurrence:',
        error
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      PaperProps={{
        sx: {
          maxWidth: '510px'
        }
      }}
      title=""
      setIsOpenDialog={onClose}
      isOpenDialog={isOpen}
    >
      <form onSubmit={handleSubmit(onSubmit, toastFirstError)}>
        <Stack alignItems="center" gap={2} mt="-45px">
          <IconContainer icon="close" />
          <Box>
            <Typography variant="smBold">
              {type === 'charge'
                ? 'Confirma o cancelamento dessa fatura?'
                : type === 'recurrence'
                ? 'Confirma o cancelamento dessa recorrência?'
                : 'Confirma o cancelamento desse parcelamento?'}
            </Typography>
          </Box>

          <Stack width="100%" mt={1} gap={0.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography>Contabilizar nas métricas?</Typography>

              <Controller
                name="countAsChurn"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch checked={value} onChange={onChange} />
                )}
              />
            </Stack>
            <Stack
              direction="row"
              gap={1}
              mt={1}
              p={1.5}
              borderRadius="8px"
              sx={{ background: rgba(theme.palette.info[300], 0.1) }}
            >
              <Box sx={{ flexShrink: 0, mt: 0.2 }}>
                <Icon
                  icon="info"
                  width="18px"
                  height="18px"
                  colorSX={theme.palette.info[200]}
                />
              </Box>
              <Typography variant="smLight" color={theme.palette.neutral[200]}>
                {METRICS_HELPER_TEXT[type]}
              </Typography>
            </Stack>
          </Stack>

          {showCancelNfsesToggle && (
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>Cancelar também a nota fiscal?</Typography>
                  <Tooltip
                    placement="top"
                    arrow
                    title="O cancelamento da nota fiscal é irreversível e segue regras de prazo da prefeitura."
                  >
                    <Box sx={{ mt: 0.4 }}>
                      <Icon
                        icon="info"
                        width="18px"
                        height="18px"
                        colorSX={theme.palette.neutral[700]}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </Stack>

              <Controller
                name="cancelNfses"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch checked={!!value} onChange={onChange} />
                )}
              />
            </Stack>
          )}

          <Stack>
            <Stack gap={3}>
              <Accordion defaultExpanded={false}>
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="smBold">
                      Motivo de cancelamento
                    </Typography>
                    <Tooltip
                      placement="top"
                      arrow
                      title="É uma informação interna. O que define as métricas é a opção acima."
                    >
                      <Box sx={{ mt: 0.4 }}>
                        <Icon
                          icon="info"
                          width="18px"
                          height="18px"
                          colorSX={theme.palette.neutral[700]}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Stack gap={2}>
                    {/* Reason section */}
                    <Box>
                      <Controller
                        name="cancellationReason"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup {...field}>
                            <FormControlLabel
                              value="wrong_billing"
                              control={<Radio />}
                              label={
                                <Typography>
                                  Cobrança indevida{' '}
                                  <Typography
                                    color={theme.palette.neutral[400]}
                                  >
                                    (cliente não deveria ser cobrado)
                                  </Typography>
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              value="issue_error"
                              control={<Radio />}
                              label={
                                <Typography>
                                  Erro na emissão{' '}
                                  <Typography
                                    color={theme.palette.neutral[400]}
                                  >
                                    (cliente receberá outra cobrança)
                                  </Typography>
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              value="service_cancellation"
                              control={<Radio />}
                              label={
                                <Typography>
                                  Cliente cancelou a prestação de serviços{' '}
                                  <Typography
                                    color={theme.palette.neutral[400]}
                                  >
                                    (churn voluntário)
                                  </Typography>
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              value="non_payment"
                              control={<Radio />}
                              label={
                                <Typography>
                                  Cliente inadimplente{' '}
                                  <Typography
                                    color={theme.palette.neutral[400]}
                                  >
                                    (churn involuntário)
                                  </Typography>
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              value="other"
                              control={<Radio />}
                              label={
                                <Stack direction="row" gap={0.2}>
                                  <Typography>Outros motivos</Typography>
                                  <Typography
                                    color={theme.palette.neutral[400]}
                                  >
                                    (especificar abaixo)
                                  </Typography>
                                </Stack>
                              }
                            />
                          </RadioGroup>
                        )}
                      />
                      {errors.cancellationReason && (
                        <Typography color="error" variant="smLight">
                          {errors.cancellationReason.message}
                        </Typography>
                      )}
                    </Box>

                    {/* Observations section */}
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <Typography>Observações</Typography>
                          <Tooltip
                            placement="top"
                            arrow
                            title={
                              type === 'charge'
                                ? 'Essa informação ficará visível nos detalhes da cobrança'
                                : type === 'recurrence'
                                ? 'Essa informação ficará visível nos detalhes da recorrência'
                                : 'Essa informação ficará visível nos detalhes do parcelamento'
                            }
                          >
                            <Box sx={{ mt: 0.4 }}>
                              <Icon
                                icon="info"
                                width="18px"
                                height="18px"
                                colorSX={theme.palette.neutral[700]}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      </Stack>

                      <Controller
                        name="notes"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <Textarea
                            fullWidth
                            style={{
                              height: '80px',
                              resize: 'vertical',
                              maxHeight: '50vh'
                            }}
                            placeholder="Descreva o motivo do cancelamento e outras anotações relevantes"
                            error={!!error}
                            helperText={error?.message}
                            {...field}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            mt={2}
            gap={1}
            width="100%"
            justifyContent="end"
          >
            <Button
              variantButton="neutral"
              onClick={onClose}
              disabled={loading}
              type="button"
              fullWidth
            >
              Voltar
            </Button>
            <Button
              fullWidth
              variantButton="primary"
              type="submit"
              disabled={loading}
              loading={loading}
            >
              {cancellationData?.uuid ? 'Salvar' : 'Cancelar'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  )
}

export default CancelConfirmationModal
